import {
	columnVisibilityFeature,
	createColumnHelper,
	metaHelper,
	rowSortingFeature,
	tableFeatures,
} from '@tanstack/react-table';
import { EditIcon, KeyIcon } from 'lucide-react';

import { getRoleBadgeColor, type RoleType } from '@/common/permission';
import type { User } from '@/common/user';
import { DataTableColumnHeader } from '@/components/data-table/data-table-column-header';
import type { DataTableColumnMeta } from '@/components/data-table/data-table-features';
import type { SortFieldMap } from '@/components/data-table/sort-param';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { t } from '@/i18n';
import { cn } from '@/lib/utils';

export interface UserTableMeta {
	rolesById: Record<string, RoleType>;
	onToggleStatus: (user: User) => void;
	onEdit: (user: User) => void;
	onManagePassword: (user: User) => void;
}

// Sorting is server-side (`manualSorting`), so no sorted row model or sort fns are registered.
export const userTableFeatures = tableFeatures({
	columnVisibilityFeature,
	rowSortingFeature,
	tableMeta: metaHelper<UserTableMeta>(),
	columnMeta: metaHelper<DataTableColumnMeta>(),
});

// Column id → backend `sort` field(s). Must match USER_SORT_FIELDS in the backend DTO.
// `User.name` is empty for users created through the API, so Name sorts by first then last name.
export const USER_SORT_FIELDS: SortFieldMap = {
	name: ['firstName', 'lastName'],
	email: 'email',
	status: 'enabled',
};

const columnHelper = createColumnHelper<typeof userTableFeatures, User>();

export const userColumns = columnHelper.columns([
	columnHelper.accessor((row) => `${row.firstName ?? ''} ${row.lastName ?? ''}`.trim(), {
		id: 'name',
		header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.labels.name')} />,
		cell: ({ row }) => (
			<span className="font-medium">
				{row.original.firstName} {row.original.lastName}
			</span>
		),
	}),
	columnHelper.accessor('email', {
		id: 'email',
		header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.labels.email')} />,
	}),
	// Roles are Keycloak group ids, not a column on User, so the API can't sort by them.
	columnHelper.accessor('groups', {
		id: 'role',
		enableSorting: false,
		header: () => t('common.labels.role'),
		cell: ({ row, table }) => {
			const rolesById = table.options.meta?.rolesById ?? {};
			return (
				<div className="flex flex-wrap gap-1.5">
					{row.original.groups.map((role) => (
						<span
							key={role}
							className={cn(
								'inline-flex items-center px-2 py-0.5 text-xs font-medium border rounded',
								getRoleBadgeColor(rolesById[role]?.name || ''),
							)}
						>
							{rolesById[role]?.name || ''}
						</span>
					))}
				</div>
			);
		},
	}),
	columnHelper.accessor('enabled', {
		id: 'status',
		header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.labels.status')} />,
		cell: ({ row, table }) => (
			<div className="flex items-center gap-2">
				<Switch
					checked={row.original.enabled}
					onCheckedChange={() => table.options.meta?.onToggleStatus(row.original)}
					className="data-[state=checked]:bg-green-600"
				/>
				<span className="text-sm text-muted-foreground">
					{row.original.enabled ? t('common.states.active') : t('common.states.inactive')}
				</span>
			</div>
		),
	}),
	columnHelper.display({
		id: 'actions',
		header: () => t('common.labels.actions'),
		cell: ({ row, table }) => {
			const meta = table.options.meta;
			if (!meta) return null;
			return (
				<div className="flex items-center justify-end gap-2">
					<Button
						className="hover:bg-blue-50 hover:text-primary"
						variant="ghost"
						size="sm"
						onClick={() => meta.onEdit(row.original)}
					>
						<EditIcon className="size-4" />
					</Button>
					<Button
						className="hover:bg-blue-50 hover:text-primary"
						variant="ghost"
						size="sm"
						onClick={() => meta.onManagePassword(row.original)}
					>
						<KeyIcon className="size-4" />
					</Button>
				</div>
			);
		},
		meta: { className: 'text-right', headerClassName: 'text-right' },
	}),
]);
